/**
 * RENDA-K のプリセットストリップ。
 *
 * 構成・挙動は PHRASE BLOOM の PresetBar と同じ:
 *   [保存] [プリセット名（クリックでリネーム）▼] [<] [>] [削除]
 *
 * - 保存: 保存状態から変更があるとインクピンクに点灯。クリックで新規保存、
 *   自分のプリセットを開いている場合は「上書き / 新規」を選べる
 * - ▼: 2 階層ドロップダウン（MY PRESETS / 相互フォローの共有プリセット）
 * - < >: 選択中ソースのプリセットを順に読み込む（末尾でループ）
 *
 * ロジックは汎用フック `useToolPresetBrowser` に分離されており、
 * このコンポーネントはクリーム地 + 濃紺縁取りのトイガン風の見た目のみを持つ。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import ConfirmModal from "../../../../components/modal/ConfirmModal";
import ToolTip from "../../../../components/tooltip/ToolTip";
import {
  useToolPresetBrowser,
  type UseToolPresetBrowserArgs,
} from "../../hooks/useToolPresetBrowser";
import { RENDA_COLORS, RENDA_FONT, RENDA_PANEL } from "../palette";

// ===== スタイル =====

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

/** ストリップ用の小型ボタン（アイコン 1 つ、ステッカー風）。 */
const StripButton = styled.button<{ $lit?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 2.5px solid ${RENDA_PANEL.outline};
  border-radius: 12px;
  background: #fffdf6;
  color: ${({ $lit }) => ($lit ? RENDA_COLORS.inkPink : RENDA_PANEL.engraving)};
  cursor: pointer;
  box-shadow: 0 3px 0 rgba(37, 42, 64, 0.35);
  transition:
    color 0.15s ease-out,
    transform 0.06s ease-out,
    box-shadow 0.06s ease-out;

  .material-symbols-rounded {
    font-size: 20px;
  }

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }

  &:active:not(:disabled) {
    transform: translateY(2px);
    box-shadow: 0 1px 0 rgba(37, 42, 64, 0.35);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${RENDA_PANEL.focus};
    outline-offset: 2px;
  }
`;

/** インクタンク風のプリセット名ディスプレイ（濃紺ピル）。 */
const Display = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 180px;
  max-width: 360px;
  height: 34px;
  padding: 0 4px 0 12px;
  background: ${RENDA_PANEL.displayBg};
  border: 2.5px solid ${RENDA_PANEL.outline};
  border-radius: 999px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.55);
`;

const NameButton = styled.button`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.05em;
  color: ${RENDA_COLORS.inkYellow};
  cursor: text;

  &:focus-visible {
    outline: 1px solid ${RENDA_COLORS.inkYellow};
    outline-offset: 2px;
  }
`;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  padding: 0;
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.05em;
  color: ${RENDA_PANEL.displayText};

  &:focus {
    outline: none;
  }
`;

const CaretButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 999px;
  background: none;
  color: rgba(238, 247, 255, 0.55);
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 20px;
  }

  &:hover {
    color: ${RENDA_PANEL.displayText};
  }

  &:focus-visible {
    outline: 1px solid ${RENDA_COLORS.inkYellow};
  }
`;

/** ドロップダウンメニュー（ソース一覧 / 保存アクション共用）。 */
const Menu = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  max-height: 320px;
  overflow-y: auto;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: ${RENDA_PANEL.face};
  border: 2.5px solid ${RENDA_PANEL.outline};
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(37, 42, 64, 0.35);
  z-index: 20;
`;

const MenuItem = styled.li<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.08em;
  white-space: nowrap;
  color: ${RENDA_PANEL.engravingStrong};
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 16px;
    color: ${({ $active }) => ($active ? RENDA_COLORS.inkPink : "transparent")};
  }

  &:hover {
    background: rgba(37, 42, 64, 0.08);
  }
`;

/** ソース行（ユーザー名 + 展開シェブロン）。 */
const SourceItem = styled(MenuItem)`
  justify-content: space-between;

  /* 展開シェブロン（行直下の末尾アイコン）は常に表示 */
  > .material-symbols-rounded:last-child {
    color: ${RENDA_PANEL.engraving};
  }
`;

const SourceLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 下階層のプリセット名行。 */
const PresetItem = styled(MenuItem)`
  padding-left: 34px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${RENDA_PANEL.engraving};
`;

const EmptyItem = styled.li`
  padding: 8px 14px 8px 34px;
  font-family: ${RENDA_FONT};
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${RENDA_PANEL.tick};
`;

/** 保存ボタンまわり（保存メニューの基準位置）。 */
const SaveGroup = styled.div`
  position: relative;
`;

const TooltipTarget = styled.span`
  display: inline-flex;
`;

// ===== 本体 =====

export type PresetBarProps = UseToolPresetBrowserArgs;

const PresetBar: React.FC<PresetBarProps> = (props) => {
  const browser = useToolPresetBrowser(props);

  const [openMenu, setOpenMenu] = useState<"source" | "save" | null>(null);
  /** ソースメニューで下階層（プリセット名）を展開しているソース。 */
  const [expandedSourceId, setExpandedSourceId] = useState<
    "me" | number | null
  >(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!openMenu) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [openMenu]);

  const beginRename = useCallback(() => {
    setNameDraft(browser.name);
    setIsEditingName(true);
  }, [browser.name]);

  const commitRename = useCallback(() => {
    setIsEditingName(false);
    browser.rename(nameDraft);
  }, [browser, nameDraft]);

  const handleSaveClick = useCallback(() => {
    if (!browser.isSavable || browser.isSaving) return;
    if (browser.ownLoaded) {
      // 上書き / 新規 の選択メニューを開く
      setOpenMenu((prev) => (prev === "save" ? null : "save"));
    } else {
      browser.saveAsNew();
    }
  }, [browser]);

  return (
    <Bar ref={rootRef}>
      <SaveGroup>
        <ToolTip
          description={
            browser.ownLoaded
              ? "保存（上書き / 新規）"
              : "自分のプリセットとして保存"
          }
        >
          <TooltipTarget>
            <StripButton
              type="button"
              $lit={browser.isSavable}
              disabled={!browser.isSavable || browser.isSaving}
              aria-label="プリセットを保存"
              onClick={handleSaveClick}
            >
              <span className="material-symbols-rounded">save</span>
            </StripButton>
          </TooltipTarget>
        </ToolTip>

        {openMenu === "save" && (
          <Menu>
            <MenuItem
              onClick={() => {
                setOpenMenu(null);
                browser.overwrite();
              }}
            >
              <span className="material-symbols-rounded">check</span>
              OVERWRITE
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenMenu(null);
                browser.saveAsNew();
              }}
            >
              <span className="material-symbols-rounded">check</span>
              SAVE AS NEW
            </MenuItem>
          </Menu>
        )}
      </SaveGroup>

      <Display>
        {isEditingName ? (
          <NameInput
            autoFocus
            value={nameDraft}
            maxLength={100}
            aria-label="プリセット名"
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") setIsEditingName(false);
            }}
          />
        ) : (
          <ToolTip description="クリックで名前を変更">
            <NameButton
              type="button"
              aria-label={`${browser.name}の名前を変更`}
              onClick={beginRename}
            >
              {browser.name}
            </NameButton>
          </ToolTip>
        )}

        <ToolTip description="プリセットを選択">
          <CaretButton
            type="button"
            aria-label="プリセットを選択"
            onClick={() =>
              setOpenMenu((prev) => {
                if (prev === "source") return null;
                // 開くとき、現在のソースを展開した状態で始める
                setExpandedSourceId(browser.sourceId);
                return "source";
              })
            }
          >
            <span className="material-symbols-rounded">arrow_drop_down</span>
          </CaretButton>
        </ToolTip>

        {openMenu === "source" && (
          <Menu>
            {browser.sources.map((source) => {
              const expanded = expandedSourceId === source.id;
              return (
                <React.Fragment key={String(source.id)}>
                  <SourceItem
                    $active={source.id === browser.sourceId}
                    onClick={() =>
                      setExpandedSourceId(expanded ? null : source.id)
                    }
                  >
                    <SourceLabel>
                      <span className="material-symbols-rounded">check</span>
                      {source.label}
                    </SourceLabel>
                    <span className="material-symbols-rounded">
                      {expanded ? "expand_more" : "chevron_right"}
                    </span>
                  </SourceItem>

                  {expanded &&
                    (source.presets.length > 0 ? (
                      source.presets.map((preset) => (
                        <PresetItem
                          key={preset.id}
                          $active={
                            source.id === browser.sourceId &&
                            preset.id === browser.currentPresetId
                          }
                          onClick={() => {
                            setOpenMenu(null);
                            browser.selectPreset(source.id, preset.id);
                          }}
                        >
                          <span className="material-symbols-rounded">
                            check
                          </span>
                          {preset.name}
                        </PresetItem>
                      ))
                    ) : (
                      <EmptyItem>NO PRESETS</EmptyItem>
                    ))}
                </React.Fragment>
              );
            })}
          </Menu>
        )}
      </Display>

      <ToolTip description="前のプリセット">
        <TooltipTarget>
          <StripButton
            type="button"
            disabled={!browser.hasPresets}
            aria-label="前のプリセット"
            onClick={browser.goPrev}
          >
            <span className="material-symbols-rounded">chevron_left</span>
          </StripButton>
        </TooltipTarget>
      </ToolTip>
      <ToolTip description="次のプリセット">
        <TooltipTarget>
          <StripButton
            type="button"
            disabled={!browser.hasPresets}
            aria-label="次のプリセット"
            onClick={browser.goNext}
          >
            <span className="material-symbols-rounded">chevron_right</span>
          </StripButton>
        </TooltipTarget>
      </ToolTip>

      <ToolTip description="このプリセットを削除">
        <TooltipTarget>
          <StripButton
            type="button"
            disabled={!browser.ownLoaded || browser.isDeleting}
            aria-label="プリセットを削除"
            onClick={() => setConfirmingDelete(true)}
          >
            <span className="material-symbols-rounded">delete</span>
          </StripButton>
        </TooltipTarget>
      </ToolTip>

      {confirmingDelete && (
        <ConfirmModal
          message={`「${browser.name}」を削除しますか？`}
          confirmLabel="削除"
          onConfirm={() => {
            setConfirmingDelete(false);
            browser.deleteCurrent();
          }}
          onCancel={() => setConfirmingDelete(false)}
          loading={browser.isDeleting}
        />
      )}
    </Bar>
  );
};

export default PresetBar;
